import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiClock, FiCheck, FiPhone, FiArrowLeft, FiNavigation, FiCoffee, FiShield } = FiIcons;

function LunchStop() {
  const features = [
    { icon: FiUsers, title: "50+ Guest Capacity", desc: "Spacious dining area for large tour groups" },
    { icon: FiClock, title: "Quick Service", desc: "Buffet ready in just 15 minutes" },
    { icon: FiShield, title: "SLTDA Certified", desc: "Superior Bungalow classification ensures quality" },
    { icon: FiCoffee, title: "Driver Facilities", desc: "Dedicated lounge with complimentary meals" }
  ];

  const inclusions = [
    "Traditional Sabaragamuwa buffet spread",
    "Clean, Western-standard restrooms", 
    "Ample bus parking space",
    "Driver lounge with free meals",
    "Fast, efficient service",
    "Air-conditioned dining area",
    "Local specialty dishes",
    "Vegetarian options available"
  ];

  return (
    <div className="min-h-screen pt-20">
      <div className="liquid-bg"></div>
      
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <SafeIcon icon={FiArrowLeft} />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Perfect <span className="text-gradient">Lunch Stop</span>
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              The ideal midway refreshment point for tour groups traveling between Embilipitiya and Maduwanwela Walawwa. 
              Experience authentic Sabaragamuwa hospitality with superior facilities and lightning-fast service.
            </p>
          </motion.div>

          <motion.div
            className="glass-card p-8 md:p-12 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">Why Choose Kumarana?</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiNavigation} className="text-blue-300 mt-1" />
                    <div>
                      <div className="font-semibold">Strategic Location</div>
                      <div className="text-white/80 text-sm">Perfectly positioned at Panamure Junction on the route to Maduwanwela</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiClock} className="text-green-300 mt-1" />
                    <div>
                      <div className="font-semibold">Perfect Timing</div>
                      <div className="text-white/80 text-sm">Ideal lunch stop after morning Udawalawe safari or Maduwanwela visit</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiShield} className="text-yellow-300 mt-1" />
                    <div>
                      <div className="font-semibold">Quality Assured</div>
                      <div className="text-white/80 text-sm">SLTDA Superior Bungalow certification guarantees standards</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="glass-card p-6">
                <h3 className="text-xl font-semibold mb-4 text-center">Pricing</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 glass-card rounded-lg">
                    <span>Adult Buffet</span>
                    <span className="text-blue-300 font-bold">LKR 1,800</span>
                  </div>
                  <div className="flex justify-between items-center p-3 glass-card rounded-lg">
                    <span>Child (5-12 years)</span>
                    <span className="text-blue-300 font-bold">LKR 900</span>
                  </div>
                  <div className="flex justify-between items-center p-3 glass-card rounded-lg">
                    <span>Driver Meals</span>
                    <span className="text-green-400 font-bold">Complimentary</span>
                  </div>
                </div>
                <div className="mt-4 text-center text-sm text-white/80">
                  Group discounts available for 20+ guests
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="pb-20">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="glass-card p-6 text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 glass-card rounded-full flex items-center justify-center">
                  <SafeIcon icon={feature.icon} className="text-2xl text-blue-300" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-white/80 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              className="glass-card p-8"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold mb-6">What's Included</h3>
              <div className="space-y-3">
                {inclusions.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="glass-card p-6">
                <h4 className="text-xl font-semibold mb-4">Book Your Group Lunch</h4>
                <p className="text-white/80 mb-6">
                  Contact us to reserve your lunch stop. We recommend booking in advance 
                  to ensure the best service for your group.
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:076-8727112"
                    className="btn-glass w-full justify-center pulse-glow"
                  >
                    <SafeIcon icon={FiPhone} />
                    Call Now: 076-8727112
                  </a>
                  <a 
                    href="https://wa.me/94768727112?text=Hi, I'd like to book a group lunch at Kumarana Hotel. Group size: [Please specify]"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-glass w-full justify-center"
                  >
                    Book via WhatsApp
                  </a>
                </div>
              </div>

              <div className="glass-card p-6">
                <h4 className="text-lg font-semibold mb-4">Driver Benefits</h4>
                <ul className="space-y-2 text-white/80">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Complimentary meals for drivers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Dedicated driver lounge with AC</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Mobile charging facilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Clean, private restroom access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Loyalty program with rewards</span>
                  </li>
                </ul>
              </div>

              <div className="glass-card p-6">
                <h4 className="text-lg font-semibold mb-4">Location Details</h4>
                <div className="space-y-2 text-white/80 text-sm">
                  <div><strong>Distance from Embilipitiya:</strong> 20 km (25 minutes)</div>
                  <div><strong>Distance to Maduwanwela:</strong> 15 km (20 minutes)</div>
                  <div><strong>Bus Parking:</strong> Space for multiple tour buses</div>
                  <div><strong>Service Hours:</strong> 6:00 AM - 10:00 PM daily</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LunchStop;